/**
 * 订阅逻辑 Hook
 */
import { computed, ref } from 'vue'
import type { Book, MemberTopupSubscribeProduct } from '../configs/types'
import { PRODUCT_LIST_API, PaymentMethodEnum, SUBSCRIBE_API_ENDPOINT } from '../constants'
import { getNaturalUserSubscribeText } from '../utils/subscribeTextOverride'
import { getSubscribeTrackingPrice } from '../utils/subscribeTrackingPrice'
import {
  createAvailablePaymentMethods,
  createSelectPaymentMethod,
  getPayType,
  getPaymentChannelName,
  initializePaymentMethod,
  isExternalPaymentRedirectPending,
  markExternalPaymentRedirect,
  markRecommendParam,
} from './paymentUtils'
import { api } from '@/api'
import { $formatErrorMessage, $getToken } from '@/helpers'
import { VIP_TYPE } from '@/constants'
import { SubscriptionCycle } from '@/utils/enum'
import { useLoadingStore, useToastStore, useUserStore } from '@/stores'
import { useI18n } from '@/composables/useI18n'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { useAxonAddToCart } from '@/composables/axon/useAxonAddToCart'
import { useAxonBeginCheckout } from '@/composables/axon/useAxonBeginCheckout'
import { useAxonPurchase } from '@/composables/axon/useAxonPurchase'
import { useFbqInitiateCheckout } from '@/composables/fbq/useFbqInitiateCheckout'
import { features } from '@/config/brands'
import { clearRetentionCheckoutPending, markRetentionCheckoutPending } from '@/utils/retentionCheckoutStorage'

interface PaymentBehaviorOptions {
  markRecommendOnPay?: boolean
  /** 在提交时动态获取支付完成后的回跳地址 */
  getSuccessUrl?: () => string
  /** 跳转外部支付页前记录挽留弹窗的时机 3 */
  markRetentionCheckout?: boolean
}

export function useSubscription(Scenario: string, book?: Book, options: PaymentBehaviorOptions = {}) {
  const markRecommendOnPay = options.markRecommendOnPay ?? true
  const shouldMarkRetentionCheckout = features?.enableRetentionSubscriptionPopup === true
    && (!!book?.contentId || options.markRetentionCheckout === true)
  const subscribeSuccess = ref(false)
  const loaded = ref(false)
  const error = ref<string | null>(null)
  const isSubmitting = ref(false)
  let successUrl = options.getSuccessUrl?.() ?? window.location.href
  const { elementClickForPay } = useElementClick()

  // 充值中心
  if (!book?.contentId && !options.getSuccessUrl)
    successUrl = `${window.location.origin}/member`

  const { $toast, $clearToast } = useToastStore()
  const { $showLoading, $hideLoading } = useLoadingStore()
  const userStore = useUserStore()
  const { t } = useI18n()
  const { reportAddToCart } = useAxonAddToCart()
  const { reportBeginCheckout } = useAxonBeginCheckout()
  const { reportPurchase } = useAxonPurchase()
  const { reportInitiateCheckout } = useFbqInitiateCheckout()

  // 多支付方式相关状态
  const currentPaymentMethod = ref<number>(PaymentMethodEnum.PAYPAL) // 默认 PayPal
  const productLists = ref<any[]>([]) // 按支付方式分组的商品列表
  const retainSubscribeProductLists = ref<any[]>([]) // 订阅挽留弹窗商品列表
  const hasPayPalPasswordlessPayment = ref(false)
  const hasPlacedOrder = ref(false)

  /**
   * 初始化支付方式
   */
  function initPaymentMethod() {
    initializePaymentMethod(
      currentPaymentMethod,
      productLists,
      hasPayPalPasswordlessPayment,
      hasPlacedOrder,
    )
  }

  const rawAvailablePaymentMethods = computed(createAvailablePaymentMethods(
    productLists,
    hasPayPalPasswordlessPayment,
    hasPlacedOrder,
  ))

  /**
   * 非广告用户(landingPageId 为空,账号级)的畅读会员商品覆写为客户端价格说明文案(ZKSN-251)；
   * 广告用户、折扣会员、免费档保持接口返回的 subscribeText
   */
  function applyNaturalUserSubscribeText(list: any) {
    if (userStore.$state.landingPageId || !Array.isArray(list?.products))
      return list

    const products = list.products.map((item: MemberTopupSubscribeProduct) => {
      const text = getNaturalUserSubscribeText(item, t)
      // subscribeTextOverridden 标记供渲染层区分:覆写文案用 BasicFitText 缩放展示,接口文案走截断兜底
      return text ? { ...item, subscribeText: text, subscribeTextOverridden: true } : item
    })
    return { ...list, products }
  }

  /**
   * 可用的支付方式列表（订阅页面包含 Stripe）
   */
  const availablePaymentMethods = computed(
    () => rawAvailablePaymentMethods.value.map(applyNaturalUserSubscribeText),
  )

  /**
   * 当前支付方式的订阅商品列表（有 cycle 且大于 0）
   *
   * 从 productLists 中找到对应支付方式的商品列表，然后过滤出订阅商品
   */
  const currentSubscriptionProducts = computed(() => {
    const currentMethod = Number(currentPaymentMethod.value)

    // 从 productLists 中找到对应支付方式的商品列表
    const productList = availablePaymentMethods.value.find(
      list => Number(list.paymentMethod) === currentMethod,
    )

    if (!productList || !productList.products)
      return []

    return productList.products
  })

  /**
   * subscribeProducts 兼容旧代码，使用 currentSubscriptionProducts
   */
  const subscribeProducts = computed(() => currentSubscriptionProducts.value)

  const currentRetentionSubscriptionProducts = computed(() => {
    const currentMethod = Number(currentPaymentMethod.value)
    const productList = retainSubscribeProductLists.value.find(
      list => Number(list.paymentMethod) === currentMethod,
    )

    if (!productList || !productList.products)
      return []

    // 挽留弹窗与订阅页复用同一套档位描述，确保自然用户的 subscribeText 覆写规则一致。
    return applyNaturalUserSubscribeText(productList).products
  })

  const getSubscriptList = async function () {
    // 未登录时跳过订阅档位请求（如首页加载时登录尚未完成），避免不必要的 401 报错。
    // 登录后阅读页的 useSubscription 会重新发起请求。
    if (!$getToken()) {
      loaded.value = true
      return
    }

    try {
      const res: any = await api.get(PRODUCT_LIST_API.SUBSCRIBE)

      // 解析多支付方式数据
      if (res.data && typeof res.data === 'object') {
        hasPayPalPasswordlessPayment.value = res.data.hasPayPalPasswordlessPayment || false
        hasPlacedOrder.value = res.data.hasPlacedOrder || false
        productLists.value = res.data.productLists || []
        retainSubscribeProductLists.value = res.data.retainSubscribeProductLists || []

        // 初始化默认支付方式
        initPaymentMethod()
      }
    }
    catch (err) {
      const errorMsg = $formatErrorMessage(err)
      if (!isExternalPaymentRedirectPending()) {
        error.value = errorMsg
        $toast(errorMsg)
      }
    }
    loaded.value = true
  }

  const getUnitPrice = function (item: MemberTopupSubscribeProduct) {
    if (!item.cycle)
      return ''

    // 季度/半年/整年统一折算为每日单价（天数取近似: 季度90天, 半年183天, 整年365天）
    if (item.cycle === SubscriptionCycle.YEAR)
      return `${(Number(item.price) / 365).toFixed(2)} / Day`

    if (item.cycle === SubscriptionCycle.HALF_YEAR)
      return `${(Number(item.price) / 183).toFixed(2)} / Day`

    if (item.cycle === SubscriptionCycle.SEASON)
      return `${(Number(item.price) / 90).toFixed(2)} / Day`

    if (item.cycle === SubscriptionCycle.MONTH)
      return `${(Number(item.price) / 30).toFixed(2)} / Day`

    if (item.cycle === SubscriptionCycle.WEEK)
      return `${(Number(item.price) / 7).toFixed(2)} / Day`

    if (item.cycle === SubscriptionCycle.ThreeDays)
      return `${(Number(item.price) / 3).toFixed(2)} / Day`

    return ''
  }

  function isZero(val: string | number | null | undefined) {
    if (val === null || val === undefined || val === '')
      return false

    if (Number.parseFloat(val as any) === 0)
      return true

    return false
  }

  function getIsImageBackground(item: any) {
    if (item.subscribeType === VIP_TYPE.DISCOUNT)
      return true

    if (isZero(item.preferentialPrice) || item.preferentialPrice)
      return true

    return false
  }
  const isReloadPage = function () {
    // 无数据时的处理逻辑
    const currentUrl = new URL(window.location.href)
    if (book?.contentId) {
      // 在阅读页面时，移除 isShowpopupRecharge 参数后刷新页面，更新章节解锁状态
      currentUrl.searchParams.delete('isShowpopupRecharge')
      // 直接支付成功（免密）已解锁，清除 recommend 标记，避免重载后误展示推荐模块
      currentUrl.searchParams.delete('recommend')
      currentUrl.searchParams.delete('recommendChapterId')
      window.location.href = currentUrl.href
    }
    else {
      window.location.reload()
    }
  }

  const onSubscribeProducts = async function (item: MemberTopupSubscribeProduct) {
    if (isSubmitting.value)
      return false

    isSubmitting.value = true
    const trackingPrice = getSubscribeTrackingPrice(item)
    elementClickForPay('subscribe', {
      purchaseId: item.subscribeConfigId,
      commodityId: item.productId,
      purchaseType: '2',
      price: item.price,
      paymentChannel: getPaymentChannelName(currentPaymentMethod.value),
      purchaseScenario: Scenario, // store chapter
      vipType: VIP_TYPE.DISCOUNT === item.subscribeType ? '2' : '1',
      vipCycle: item.cycle,
      priceDiscount: item.preferentialPrice,
      vipCycleDiscount: item.preferentialCycle,
    })

    $showLoading()
    // 全局悬浮入口可能在 SPA 路由切换后发起支付，需在提交时获取当前页面。
    if (options.getSuccessUrl)
      successUrl = options.getSuccessUrl()

    // 阅读页发起订阅前同步标记 recommend，确保未支付返回时展示推荐书籍模块（同步刷新 successUrl 作为 redirect_source）
    if (book?.contentId && markRecommendOnPay)
      successUrl = markRecommendParam(book?.contentChapterId)
    const url = `${window.location.origin}/paying?redirect_source=${encodeURIComponent(successUrl)}&paymentChannel=${getPaymentChannelName(currentPaymentMethod.value)}&axon_price=${trackingPrice}&axon_product_id=${item.productId}&axon_content_id=${book?.contentId || ''}`
    const fbEventId = reportInitiateCheckout({
      value: item.price,
      currency: 'USD',
      contentIds: [String(item.productId)],
      numItems: 1,
      dedup: true,
    })
    const params = {
      payConfigId: item.subscribeConfigId,
      productId: item.productId,
      memberCurrencyCode: 'usd',
      subscribeConfigId: item.subscribeConfigId,
      contentId: book?.contentId,
      contentChapterId: book?.contentChapterId,
      payType: getPayType(currentPaymentMethod.value),
      type: 2,
      successUrl: `${url}&payment_status=success&type=subscribe&subscribeType=${item.subscribeType}&discountOnceCoins=${item.discountOnceCoins}`,
      cancelUrl: `${url}&payment_status=cancel&type=subscribe&subscribeType=${item.subscribeType}`,
      ...(fbEventId ? { fbEventId } : {}),
    }

    try {
      const { data } = await api.post<any>(SUBSCRIBE_API_ENDPOINT, params)
      reportAddToCart(item.productId, book?.contentId, 'Membership', trackingPrice)
      reportBeginCheckout(item.productId, book?.contentId, 'Membership', trackingPrice)
      if (data) {
        // 判断 data 是否为有效的 URL
        try {
          const _ = new URL(data)
          subscribeSuccess.value = true
          if (shouldMarkRetentionCheckout)
            markRetentionCheckoutPending()
          markExternalPaymentRedirect()
          $clearToast()
          window.location.href = data
          return true
        }
        catch {
          // 直接支付成功（免密），不经 /paying 验单页,purchase 在此上报,data 为后端返回的订单标识;
          // 延迟刷新给 GTM 留出发送时间,避免事件随页面跳转丢失
          if (shouldMarkRetentionCheckout)
            clearRetentionCheckoutPending()
          reportPurchase(item.productId, book?.contentId, 'Membership', trackingPrice, String(data))
          setTimeout(() => isReloadPage(), 1500)
        }
      }
      else {
        if (shouldMarkRetentionCheckout)
          clearRetentionCheckoutPending()
        // 无数据时的处理逻辑
        isReloadPage()
        return true
      }
    }
    catch (error) {
      if (shouldMarkRetentionCheckout)
        clearRetentionCheckoutPending()
      const errorMsg = $formatErrorMessage(error)
      $toast(errorMsg)
      return false
    }
    finally {
      isSubmitting.value = false
      $hideLoading()
    }
  }

  /**
   * 切换支付方式
   */
  const selectPaymentMethod = createSelectPaymentMethod(currentPaymentMethod)

  getSubscriptList()

  return {
    loaded,
    subscribeProducts,
    subscribeSuccess,
    onSubscribeProducts,
    getUnitPrice,
    getIsImageBackground,
    isZero,
    // 多支付方式相关
    currentPaymentMethod, // 导出当前支付方式
    availablePaymentMethods,
    currentSubscriptionProducts,
    currentRetentionSubscriptionProducts,
    selectPaymentMethod,
    productLists, // 导出原始数据供外部使用
    retainSubscribeProductLists,
    hasPayPalPasswordlessPayment,
  }
}

// 保持向后兼容的别名导出
export const useSubscript = useSubscription
